"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Drink } from "src/lib/db";
import { useGetDrinkTypes } from "src/lib/hooks/useGetTypes";
import { addDrink, editDrink } from "src/lib/slices/ownItems";
import { v4 as uuid } from "uuid";
import * as z from "zod";
import { PhotoFormField } from "../PhotoFormField";
import { Button } from "../ui/button";
import { AlcDrawer, DrawerTitle } from "../ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const FormSchema = z.object({
  name: z.string().min(1),
  alcoholVolume: z.number().min(0).max(100),
  icon: z.string().nullable(),
  type: z.string().nullable(),
});

function AddDrinkForm({
  setIsOpen,
  drink,
}: {
  setIsOpen: (isOpen: boolean) => void;
  drink?: Drink;
}) {
  const dispatch = useDispatch();

  const form = useForm<z.infer<typeof FormSchema>>({
    mode: "onBlur",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: drink?.name ?? "",
      alcoholVolume: drink?.alcoholVolume ?? null,
      icon: drink?.icon ?? null,
      type: drink?.type ?? null,
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    data = _.omitBy(data, (v) => v === null || v === "");
    const newDrink: Drink = {
      type: "Own",
      id: drink?.id ?? uuid(),
      ownDrink: true,
      ...(data as Drink),
    };
    if (drink) dispatch(editDrink({ id: newDrink.id, newState: newDrink }));
    else dispatch(addDrink(newDrink));
    setIsOpen(false);
  }

  const drinksTypes = useGetDrinkTypes();

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Název</FormLabel>
              <Input type="text" autoComplete="name" id="name" {...field} />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Typ</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value ?? ""}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {drinksTypes.map((type) => (
                    <SelectItem value={type.key} key={type.key}>
                      {type.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={"Own"}>Nespecifikováno</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="alcoholVolume"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Obsah alkoholu (%)</FormLabel>
              <Input
                type="number"
                defaultValue={field.value ?? ""}
                onChange={(event) =>
                  field.onChange(+event.target.value || null)
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="icon"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Obrázek</FormLabel>
              <PhotoFormField field={field} />
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="py-4">
          <Button type="submit" className="w-full">
            {drink ? "Uložit" : "Přidat"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default function AddOwnDrinkDrawer({
  children,
  drink,
}: {
  children: React.ReactNode;
  drink?: Drink;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AlcDrawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      asChild
      trigger={children}
      header={
        <DrawerTitle>
          {drink ? "Úprava" : "Přidání"} vlastního nápoje
        </DrawerTitle>
      }
    >
      <AddDrinkForm setIsOpen={setIsOpen} drink={drink} />
    </AlcDrawer>
  );
}
